<template>
  <div>
    <div class="flex justify-between items-center">
      <div class="w-full lg:w-45 flex flex-1 justify-center my-4 mx-4 lg:mx-0">
        <div class="w-45">
          <img src="@/assets/images/logo-novonesis-dark.svg" alt="Novonesis" class="w-32 mb-4" />
          <h2 class="font-medium text-48 text-semantic-base mb-5" :style="{ lineHeight: '56px' }">
            Proteases <br />
            Matrix Calculator
          </h2>
          <h3 class="font-semibold text-2xl text-semantic-base mb-6">Create your account</h3>

          <form @submit.prevent="onSubmit">
            <div class="row flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-10 mb-6">
              <form-field-input
                v-model.trim="name"
                class="flex-1"
                size="medium"
                field-label="Name *"
                :validation-class="{ invalid: $v.name.$error || errors.name, valid: !$v.name.$invalid && !errors.name }"
                @blur="$v.name.$touch()"
              >
                <template #helper v-if="$v.name.$error || errors.name">
                  <span v-if="!$v.name.required" class="invalid"> Name can't be empty </span>

                  <span v-if="errors.name" class="invalid">
                    {{ errors.name[0] }}
                  </span>
                </template>
              </form-field-input>
            </div>

            <div class="row flex space-x-4 md:space-x-8 mb-6">
              <form-field-input
                v-model.trim="email"
                class="flex-1"
                size="medium"
                type="email"
                field-label="E-mail Address *"
                :validation-class="{
                  invalid: $v.email.$error || errors.email,
                  valid: !$v.email.$invalid && !errors.email,
                }"
                @blur="$v.email.$touch()"
              >
                <template #helper v-if="$v.email.$error || errors.email">
                  <span v-if="!$v.email.required" class="invalid"> Email can't be empty </span>

                  <span v-if="!$v.email.email" class="invalid"> Email isn't valid </span>

                  <span v-if="errors.email" class="invalid">
                    {{ errors.email[0] }}
                  </span>
                </template>
              </form-field-input>

              <form-field-input v-model.trim="phone" class="flex-1" size="medium" field-label="Phone Number">
              </form-field-input>
            </div>

            <div class="row flex space-x-4 md:space-x-8 mb-6">
              <form-field-input
                v-model.trim="company"
                class="flex-1"
                size="medium"
                field-label="Company *"
                :validation-class="{
                  invalid: $v.company.$error || errors.company,
                  valid: !$v.company.$invalid && !errors.company,
                }"
                @blur="$v.company.$touch()"
              >
                <template #helper v-if="$v.company.$error || errors.company">
                  <span v-if="!$v.company.required" class="invalid"> Company can't be empty </span>

                  <span v-if="errors.company" class="invalid">
                    {{ errors.company[0] }}
                  </span>
                </template>
              </form-field-input>

              <form-field-select
                v-model="country"
                class="flex-1"
                field-label="Country *"
                option-label="country"
                track-by="code"
                disabled-option-value="Select country"
                :validation-class="{
                  invalid: $v.country.$error || errors.country,
                  valid: !$v.country.$invalid && !errors.country,
                }"
                :options="countries"
              >
                <template #helper v-if="$v.country.$error || errors.country">
                  <span v-if="!$v.country.required" class="invalid"> Select Country </span>

                  <span v-if="errors.country" class="invalid">
                    {{ errors.country[0] }}
                  </span>
                </template>
              </form-field-select>
            </div>

            <div class="row flex space-x-4 md:space-x-8 mb-5">
              <form-field-input
                v-model.trim="password"
                class="flex-1"
                size="medium"
                type="password"
                field-label="New Password *"
                :validation-class="{
                  invalid: $v.password.$error || errors.password,
                  valid: !$v.password.$invalid && !errors.password,
                }"
                @blur="$v.password.$touch()"
              >
                <template #helper v-if="$v.password.$error || errors.password">
                  <span v-if="!$v.password.required" class="invalid"> Password can't be empty </span>

                  <span v-if="!$v.password.minLength" class="invalid"> min Length is 6 </span>

                  <span v-if="errors.password" class="invalid">
                    {{ errors.password[0] }}
                  </span>
                </template>
              </form-field-input>

              <form-field-input
                v-model.trim="password_confirmation"
                class="flex-1"
                size="medium"
                type="password"
                field-label="New Password *"
                :validation-class="{
                  invalid: $v.password_confirmation.$error || errors.password_confirmation,
                  valid:
                    $v.password_confirmation.sameAsPassword && !$v.password.$invalid && !errors.password_confirmation,
                }"
                @blur="$v.password_confirmation.$touch()"
              >
                <template #helper v-if="$v.password_confirmation.$error || errors.password_confirmation">
                  <span v-if="!$v.password_confirmation.required" class="invalid"> Passwords must be identical </span>

                  <span
                    v-if="!$v.password_confirmation.minLength && $v.password_confirmation.sameAsPassword"
                    class="invalid"
                  >
                    min Length is 6
                  </span>

                  <span v-if="errors.password_confirmation" class="invalid">
                    {{ errors.password_confirmation[0] }}
                  </span>
                </template>
              </form-field-input>
            </div>

            <div class="row mb-6">
              <label for="terms" class="m-0 text-sm lg:text-base text-semantic-base custom-checkbox">
                <input id="terms" type="checkbox" class="mr-2" v-model="terms" />
                <span class="checkmark" style="margin-top: 2px"></span>
                I agree to the
                <a
                  class="link font-medium text-gray-900"
                  href="https://www.novonesis.com/en/legal-notice-and-terms-use"
                  target="_blank"
                >
                  License Disclaimer & Terms of Use
                </a>
                and
                <a
                  class="link font-medium text-gray-900"
                  href="https://www.novonesis.com/en/privacy-policy"
                  target="_blank"
                  >Privacy Policy</a
                >.
              </label>
              <small v-if="$v.terms.$error" class="text-red-400 text-xs">Please agree with terms</small>
            </div>

            <div class="row border-b-2 border-semantic-base30">
              <button type="submit" class="btn-success w-full mb-4 flex justify-center items-center">
                <svg
                  width="22"
                  height="22"
                  class="mr-2"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <!-- eslint-disable-next-line max-len -->
                  <path
                    d="M14.5245 11.7048H0.89817C0.509113 11.7048 0.193359 11.3891 0.193359 11C0.193359 10.6109 0.509113 10.2952 0.89817 10.2952H14.5245C14.9135 10.2952 15.2293 10.6109 15.2293 11C15.2293 11.3891 14.9135 11.7048 14.5245 11.7048Z"
                    fill="#173630"
                  />
                  <!-- eslint-disable-next-line max-len -->
                  <path
                    d="M10.7655 15.4637C10.585 15.4637 10.4047 15.3953 10.2674 15.2571C9.99205 14.9818 9.99205 14.5354 10.2674 14.2599L13.5283 10.9991L10.2674 7.73819C9.99205 7.46287 9.99205 7.01634 10.2674 6.74102C10.5429 6.46571 10.9892 6.46571 11.2645 6.74102L15.0235 10.5001C15.2988 10.7754 15.2988 11.2218 15.0235 11.4971L11.2645 15.2561C11.1264 15.3953 10.946 15.4637 10.7655 15.4637Z"
                    fill="#173630"
                  />
                  <!-- eslint-disable max-len -->
                  <path
                    d="M11.4703 21.3373C7.19346 21.3373 3.41766 18.7643 1.84922 14.7815C1.7064 14.4206 1.88484 14.0109 2.24671 13.8681C2.60754 13.7272 3.01828 13.9029 3.1611 14.2666C4.51532 17.7052 7.77713 19.9277 11.4703 19.9277C16.3926 19.9277 20.398 15.9223 20.398 11C20.398 6.07768 16.3926 2.07234 11.4703 2.07234C7.77713 2.07234 4.51532 4.29483 3.1611 7.73337C3.01725 8.09713 2.60754 8.27282 2.24671 8.13189C1.88484 7.98907 1.7064 7.57936 1.84922 7.21853C3.41766 3.23573 7.19346 0.66272 11.4703 0.66272C17.1699 0.66272 21.8076 5.30043 21.8076 11C21.8076 16.6996 17.1699 21.3373 11.4703 21.3373Z"
                    fill="#173630"
                  />
                  <!-- eslint-disable max-len -->
                </svg>
                Register Now
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="img flex-1 hidden md:block relative w-full img-part h-full lg:w-55">
        <img src="~@/assets/images/login-bg.jpg" alt="background image" class="h-full w-full object-cover" />
      </div>
    </div>
  </div>
</template>

<script>
import FormFieldInput from '@/components/form/FormFieldInput';
import FormFieldSelect from '@/components/form/FormFieldSelect';
import { validationMixin } from 'vuelidate';
import { mapState, mapActions } from 'vuex';
import { required, minLength, email, sameAs } from 'vuelidate/lib/validators';

export default {
  name: 'Register',

  components: {
    FormFieldInput,
    FormFieldSelect,
  },

  mixins: [validationMixin],

  data: () => ({
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    password: '',
    password_confirmation: '',
    terms: false,
    errors: {},
  }),

  validations: {
    name: {
      required,
    },

    email: {
      required,
      email,
    },

    company: {
      required,
    },

    country: {
      required,
    },

    password: {
      required,
      minLength: minLength(6),
    },

    password_confirmation: {
      sameAsPassword: sameAs('password'),
    },

    terms: {
      sameAs: sameAs(() => true),
    },
  },

  mounted() {
    if (!this.countries.length) {
      this.getCountries();
    }

    this.$store.commit('setNavigation');
  },

  methods: {
    ...mapActions('countries', ['fetchCountries']),

    ...mapActions({
      signUp: 'auth/signUp',
    }),

    onSubmit() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }

      /* eslint-disable camelcase */
      const { name, email, phone, company, country, password, password_confirmation } = this;

      const formData = {
        name,
        email,
        phone,
        company,
        country,
        password,
        password_confirmation,
      };

      this.errors = {};

      this.signUp(formData)
        .then(() => {
          this.$router.push({ name: 'thankYou' });
        })
        .catch(error => {
          this.errors = (error.data && error.data.errors) || {};
        });
    },

    getCountries() {
      return this.fetchCountries();
    },
  },

  computed: {
    ...mapState({
      countries: state => state.countries.countries,
    }),
  },

  watch: {
    '$v.$invalid'() {
      this.errors = {};
    },
  },
};
</script>
